export type DetailControl = 'text' | 'checkbox' | 'textarea' | 'select';
export type DetailDateFormat = 'date' | 'datetime';
export type DetailFieldFormat = 'phone';
export interface DetailSelectOption {
	label: string;
	value: string;
}

export interface DetailFieldSlot {
	label: string;
	keys: string[];
	control?: DetailControl;
	dateFormat?: DetailDateFormat;
	format?: DetailFieldFormat;
	selectOptions?: DetailSelectOption[];
	selectPlaceholder?: string;
	editable?: boolean;
}

export type DetailRow = DetailFieldSlot | DetailFieldSlot[];

export interface DetailSection {
	id: string;
	title: string;
	subtitle: string;
	icon: string;
	rows: DetailRow[];
	wide?: boolean;
}

export const HPDAR_ACCOUNT_ID = 'HPDAR';

export const HPDAR_STATUS_DEFAULT = 'Active';

export const HPDAR_STATUS_OPTIONS: DetailSelectOption[] = [
	{ label: 'Active', value: 'Active' },
	{ label: 'Inactive', value: 'Inactive' },
];

/** Maps API / DB status values to HPDAR dropdown options; empty/null → Active. */
export function normalizeHpdarStatusValue(v: any): string {
	const s = String(v == null ? '' : v).trim();
	if (!s) {
		return HPDAR_STATUS_DEFAULT;
	}
	const lower = s.toLowerCase();
	if (
		lower === 'inactive' ||
		lower === 'i' ||
		lower === 'in' ||
		lower.indexOf('inactive') !== -1
	) {
		return 'Inactive';
	}
	if (
		lower === 'active' ||
		lower === 'a' ||
		lower === 'yes' ||
		lower === 'y' ||
		lower === '1'
	) {
		return HPDAR_STATUS_DEFAULT;
	}
	if (lower.indexOf('active') !== -1) {
		return HPDAR_STATUS_DEFAULT;
	}
	const match = HPDAR_STATUS_OPTIONS.find(
		(o) => o.value.toLowerCase() === lower
	);
	return match ? match.value : s;
}

export function isHpdarStatusActive(v: any): boolean {
	return normalizeHpdarStatusValue(v) === HPDAR_STATUS_DEFAULT;
}

export function isHpdarAccount(custId: string | undefined | null): boolean {
	return (custId || '').toString().trim().toUpperCase() === HPDAR_ACCOUNT_ID;
}

/** Summary strip for HPDAR basic detail view. */
export const HPDAR_SUMMARY_STRIP: {
	passId: string[];
	status: string[];
} = {
	passId: ['pelg_pass_id', 'pass_id'],
	status: ['pelg_status'],
};

/** Structured fields shown on the passenger detail page for HPDAR only. */
export const HPDAR_BASIC_SECTIONS: DetailSection[] = [
	{
		id: 'basic',
		title: 'Passenger Information',
		subtitle: 'Name, address, and contact details.',
		icon: 'person',
		wide: true,
		rows: [
			[{ label: 'Passenger ID', keys: ['pelg_pass_id', 'pass_id'] }],
			[
				{ label: 'First Name', keys: ['pass_first_name'] },
				{ label: 'Middle Name', keys: ['pass_middle_name'] },
				{ label: 'Last Name', keys: ['pass_last_name'] },
			],
			[
				{
					label: 'Phone Number',
					keys: ['loc_ph_nbr'],
					format: 'phone',
				},
			],
			[
				{ label: 'Street Number', keys: ['loc_str_nbr'] },
				{ label: 'Street Name', keys: ['loc_str_name'] },
				{ label: 'Apt / Room', keys: ['loc_str_apt_room'] },
			],
			[
				{ label: 'City', keys: ['loc_city'] },
				{ label: 'State', keys: ['loc_state'] },
				{ label: 'ZIP Code', keys: ['loc_zip_code'] },
			],
		],
	},
];

function collectSectionKeys(sections: DetailSection[]): Set<string> {
	const set = new Set<string>();
	const addSlot = (s: DetailFieldSlot) => s.keys.forEach((k) => set.add(k));
	for (const sec of sections) {
		for (const row of sec.rows) {
			if (Array.isArray(row)) {
				row.forEach(addSlot);
			} else {
				addSlot(row);
			}
		}
	}
	return set;
}

/** Keys tried for the summary strip (first present value wins). */
export const SUMMARY_STRIP: {
	recordNumber: string[];
	passId: string[];
	chargeNumber: string[];
	status: string[];
} = {
	recordNumber: ['pelg_rec_nbr'],
	passId: ['pelg_pass_id'],
	chargeNumber: ['pelg_cu_charge_nbr'],
	status: ['pelg_status'],
};

export const DETAIL_SECTIONS: DetailSection[] = [
	{
		id: 'eligibility',
		title: 'Pass Eligibility',
		subtitle: 'Eligibility details.',
		icon: 'security',
		rows: [
			[
				{
					label: 'Pass Eligibility Number',
					keys: ['pelg_rec_nbr'],
				},
			],
			[{ label: 'Pass ID', keys: ['pelg_pass_id'] }],
			[{ label: 'Charge Number', keys: ['pelg_cu_charge_nbr'] }],
			[
				{
					label: 'From Date & Time',
					keys: ['pelg_from_dt_tm'],
					dateFormat: 'datetime',
				},
				{
					label: 'To Date & Time',
					keys: ['pelg_to_dt_tm'],
					dateFormat: 'datetime',
				},
			],
			[
				{
					label: 'Insurance Plan Name',
					keys: ['pelg_ins_plan_name'],
				},
				{ label: 'Insurance Plan ID', keys: ['pelg_ins_plan_id'] },
			],
			[
				{
					label: 'Certification Type',
					keys: ['pelg_certification_type'],
				},
			],
			[
				{ label: 'EXTS Site', keys: ['pelg_exts_site'] },
				{ label: 'PCAP Max', keys: ['pelg_pcap_max'] },
			],
			[
				{
					label: 'PCA Allowed',
					keys: ['pelg_pca_allowed'],
					control: 'checkbox',
				},
				{
					label: 'Member Suffix',
					keys: ['pelg_member_suffix'],
				},
			],
			[
				{
					label: 'Trips/Month',
					keys: ['pelg_pass_allowed_trips_per_month'],
				},
				{
					label: 'Miles/Month',
					keys: ['pelg_pass_allowed_miles_per_month'],
				},
				{
					label: 'Preferred Prov. #',
					keys: ['pelg_pass_preferred_prov_nbr'],
				},
			],
			[
				{
					label: 'Amount/Month',
					keys: ['pelg_pass_allowed_amount_per_month'],
				},
				{
					label: 'Unit Amount',
					keys: ['pelg_pass_unit_amt'],
				},
				{
					label: 'Allowed Units',
					keys: ['pelg_pass_allowed_units'],
				},
			],
		],
	},
	{
		id: 'passenger',
		title: 'Passenger Information',
		subtitle: 'Passenger details',
		icon: 'person',
		rows: [
			[
				{ label: 'First Name', keys: ['pass_first_name'] },
				{
					label: 'Middle Name',
					keys: ['pass_middle_name'],
				},
			],
			[{ label: 'Last Name', keys: ['pass_last_name'] }],
			[{ label: 'Social Security #', keys: ['pass_soc_sec_nbr'] }],
			[
				{
					label: 'Date of Birth',
					keys: ['pass_dob'],
					dateFormat: 'date',
				},
				{
					label: 'Last Trip Date & Time',
					keys: ['pass_last_trp_dt_tm'],
					dateFormat: 'datetime',
				},
			],
			[
				{ label: 'Trip Type', keys: ['pass_tt_trip_type'] },
				{ label: 'Default Priority', keys: ['pass_def_priority'] },
			],
			[
				{
					label: 'Number of Escorts',
					keys: ['pass_nbr_escorts'],
				},
			],
			[
				{ label: 'Space Type', keys: ['pass_space_type'] },
				{ label: 'ADA', keys: ['pass_ada'] },
			],
			[
				{
					label: 'Disability Type',
					keys: ['pass_disability_type'],
					control: 'select',
					selectOptions: [
						{ label: 'Dialysis', value: 'Dialysis' },
						{ label: 'Hearing Impairment', value: 'Hearing Impairment' },
						{ label: 'Visual Impairment', value: 'Visual Impairment' },
					],
				},
				{
					label: 'Mobility Aids',
					keys: ['pass_mobility_aids'],
					control: 'select',
					selectOptions: [
						{ label: 'Ambulatory', value: 'Ambulatory' },
						{ label: 'Cane', value: 'Cane' },
						{ label: 'Crutches', value: 'Crutches' },
						{ label: 'Scooter', value: 'Scooter' },
						{ label: 'Walker', value: 'Walker' },
						{ label: 'Wheelchair', value: 'Wheelchair' },
					],
				},
			],
			[
				{ label: 'VH Attr', keys: ['pass_vh_attr'] },
				{ label: 'DR Attr', keys: ['pass_dr_attr'] },
			],
			[
				{
					label: 'Permanent',
					keys: ['pass_permanent'],
					control: 'checkbox',
				},
				{ label: 'VIP', keys: ['pass_vip_tag'], control: 'checkbox' },
				{
					label: 'Cash Trip',
					keys: ['pass_cash_trip_noshow_chrg'],
					control: 'checkbox',
				},
				{
					label: 'Block',
					keys: ['loc_block_pers'],
					control: 'checkbox',
				},
			],
		],
	},
	{
		id: 'location',
		title: 'Location Information',
		subtitle: 'Address and contact details.',
		icon: 'place',
		rows: [
			[
				{ label: 'Street Number', keys: ['loc_str_nbr'] },
				{ label: 'Street Nbr Suffix', keys: ['loc_str_nbr_suf'] },
			],
			[
				{ label: 'Street Name', keys: ['loc_str_name'] },
				{ label: 'Apt / Room', keys: ['loc_str_apt_room'] },
			],
			[
				{ label: 'City', keys: ['loc_city'] },
				{ label: 'State', keys: ['loc_state'] },
			],
			[
				{ label: 'ZIP Code', keys: ['loc_zip_code'] },
				{ label: 'ZIP Suffix', keys: ['loc_zip_suf'] },
			],
			[
				{ label: 'Longitude', keys: ['loc_lon'] },
				{ label: 'Latitude', keys: ['loc_lat'] },
			],
			[{ label: 'Map Coordinate', keys: ['loc_map_coord'] }],
			[
				{
					label: 'Phone Number',
					keys: ['loc_ph_nbr'],
					format: 'phone',
				},
				{ label: 'Extension', keys: ['loc_ph_ext'] },
			],
			[
				{ label: 'Location Type', keys: ['loc_type'] },
				{ label: 'Marketing Code', keys: ['loc_mktg_code'] },
			],
			[
				{
					label: 'Last Used',
					keys: ['loc_last_used_dt_tm'],
					dateFormat: 'datetime',
				},
				{ label: 'Fleet ID', keys: ['loc_fl_id'] },
			],
			[
				{ label: 'Zone Number', keys: ['loc_zn_nbr'] },
				{ label: 'Description', keys: ['loc_desc'] },
			],
		],
	},
	{
		id: 'location-extra',
		title: 'Additional Information',
		subtitle: 'Change history, SMS, web display, and other location attributes.',
		icon: 'info',
		wide: true,
		rows: [
			[
				{ label: 'Last Changed By', keys: ['loc_last_changed_by'] },
				{
					label: 'Last Changed',
					keys: ['loc_last_changed_dt_tm'],
					dateFormat: 'datetime',
				},
				{ label: 'Phone Type', keys: ['loc_ph_type'] },
				{ label: 'SMS Consent', keys: ['loc_sms_consent'] },
			],
			[
				{ label: 'SMS Provider', keys: ['loc_sms_provider'] },
				{ label: 'Display on Web', keys: ['loc_display_on_web'] },
				{ label: 'Auto Count', keys: ['loc_auto_cout'] },
			],
			[
				{ label: 'Used Count', keys: ['loc_used_count'] },
				{ label: 'Priority', keys: ['loc_priority'] },
				{ label: 'County Code', keys: ['loc_county_code'] },
				{ label: 'Airport Service Rules', keys: ['loc_airport_service_rules'] },
			],
			[
				{ label: 'Owes DR ID', keys: ['loc_owes_dr_id'] },
				{ label: 'Owes DR Amount', keys: ['loc_owes_dr_amt'] },
			],
			[
				{ label: 'VH Attr Rule', keys: ['loc_vh_attr_rule'] },
				{ label: 'DR Attr Rule', keys: ['loc_dr_attr_rule'] },
				{ label: 'BTTN Broadcast Only', keys: ['loc_bttn_broadcast_only'] },
			],
			[
				{
					label: 'Guaranteed Rate % Disc (Dest)',
					keys: ['loc_guar_rate_perc_disc_dest'],
				},
				{
					label: 'Guaranteed Rate % Disc (Pickup)',
					keys: ['loc_guar_rate_perc_disc_pkup'],
				},
				{ label: 'Google Place ID', keys: ['loc_google_place_id'] },
			],
			[
				{
					label: 'Internal Notes',
					keys: ['pelg_comments'],
					control: 'textarea',
				},
			],
		],
	},
];

/** Labels for any column that still lands in the dynamic “Additional” grid. */
export const ADDITIONAL_FIELD_LABELS: { [key: string]: string } = {
	pass_id: 'Pass ID',
	pelg_pass_id: 'Pass ID (eligibility)',
	pass_exts_site: 'EXTS Site (passenger)',
	pass_first_name: 'First Name',
	pass_last_name: 'Last Name',
	pelg_cu_charge_nbr: 'Charge Number',
	pelg_exts_site: 'EXTS Site (eligibility)',
};

/**
 * Columns that duplicate a value already shown elsewhere after pelg/pass/loc joins.
 * Kept out of the UI and out of the dynamic “Additional” grid (still present on the row object).
 */
export const JOIN_DUPLICATE_KEYS_RESERVED = new Set<string>([
	'pass_id',
	'pass_home_loc_id',
	'pass_exts_site',
	'loc_id',
	'loc_name',
	'loc_charge_nbr',
	'loc_exts_site',
	'loc_permanent',
	'loc_dont_update',
	'loc_passenger_name',
	'loc_adr_cmnt',
	'pass_name',
	'loc_vip_tag',
	'loc_vh_attr',
	'loc_dr_attr',
]);

/** Keys rendered in the HPDAR basic layout (omitted from the dynamic extras grid). */
export const HPDAR_RESERVED_DETAIL_KEYS: Set<string> = (() => {
	const set = collectSectionKeys(HPDAR_BASIC_SECTIONS);
	Object.values(HPDAR_SUMMARY_STRIP).forEach((arr) => arr.forEach((k) => set.add(k)));
	JOIN_DUPLICATE_KEYS_RESERVED.forEach((k) => set.add(k));
	return set;
})();

function collectAllSlotKeys(): Set<string> {
	const set = new Set<string>();
	const addSlot = (s: DetailFieldSlot) => s.keys.forEach((k) => set.add(k));
	for (const sec of DETAIL_SECTIONS) {
		for (const row of sec.rows) {
			if (Array.isArray(row)) {
				row.forEach(addSlot);
			} else {
				addSlot(row);
			}
		}
	}
	Object.values(SUMMARY_STRIP).forEach((arr) => arr.forEach((k) => set.add(k)));
	JOIN_DUPLICATE_KEYS_RESERVED.forEach((k) => set.add(k));
	return set;
}

/** Keys rendered in the structured layout or summary (omitted from the dynamic extras grid). */
export const RESERVED_DETAIL_KEYS: Set<string> = collectAllSlotKeys();
